import {QUATERNION, VECTOR3} from '../../index.js';

/**
 * Gets a new clone of given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @returns {matrix4}
 */
function clone($matrix) {

    return [...$matrix];
}

/**
 * Creates a new 4x4 matrix.
 * @param {[number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number]} $values The matrix values.
 * @returns {matrix4}
 */
function create($values) {

    return [...$values];
}

/**
 * Gets a new 4x4 matrix from given reference quaternion.
 * @param {quaternion} $quaternion The reference quaternion.
 * @returns {matrix4}
 */
function fromquaternion($quaternion) {

    const [x, y, z, w] = $quaternion;

    const matrix = identity();

    const xx = x + x;
    const yy = y + y;
    const zz = z + z;

    const xxxx = x * xx;
    const yxyx = y * xx;
    const yyyy = y * yy;
    const zxzx = z * xx;
    const zyzy = z * yy;
    const zzzz = z * zz;
    const wxwx = w * xx;
    const wywy = w * yy;
    const wzwz = w * zz;

    matrix[0] = 1 - yyyy - zzzz;
    matrix[1] = yxyx + wzwz;
    matrix[2] = zxzx - wywy;
    matrix[3] = 0;

    matrix[4] = yxyx - wzwz;
    matrix[5] = 1 - xxxx - zzzz;
    matrix[6] = zyzy + wxwx;
    matrix[7] = 0;

    matrix[8] = zxzx + wywy;
    matrix[9] = zyzy - wxwx;
    matrix[10] = 1 - xxxx - yyyy;
    matrix[11] = 0;

    matrix[12] = 0;
    matrix[13] = 0;
    matrix[14] = 0;
    matrix[15] = 1;

    return matrix;
}

/**
 * Gets a new quaternion representing the rotation of given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @returns {quaternion}
 */
function getrotation($matrix) {

    const quaternion = QUATERNION.identity();
    const scale = getscale($matrix);

    const scaleinverse = VECTOR3.identity();

    scaleinverse[0] = 1 / scale[0];
    scaleinverse[1] = 1 / scale[1];
    scaleinverse[2] = 1 / scale[2];

    const aa = $matrix[0] * scaleinverse[0];
    const ab = $matrix[1] * scaleinverse[1];
    const ac = $matrix[2] * scaleinverse[2];

    const ba = $matrix[4] * scaleinverse[0];
    const bb = $matrix[5] * scaleinverse[1];
    const bc = $matrix[6] * scaleinverse[2];

    const ca = $matrix[8] * scaleinverse[0];
    const cb = $matrix[9] * scaleinverse[1];
    const cc = $matrix[10] * scaleinverse[2];

    const trace = aa + bb + cc;

    let root;
    let rootinverse;

    if (trace > 0) {

        root = Math.sqrt(trace + 1) * 2;
        rootinverse = 1 / root;

        quaternion[0] = (bc - cb) * rootinverse;
        quaternion[1] = (ca - ac) * rootinverse;
        quaternion[2] = (ab - ba) * rootinverse;
        quaternion[3] = 0.25 * root;

        return quaternion;
    }

    if (aa > bb && aa > cc) {

        root = Math.sqrt(1 + aa - bb - cc) * 2;
        rootinverse = 1 / root;

        quaternion[0] = 0.25 * root;
        quaternion[1] = (ab + ba) * rootinverse;
        quaternion[2] = (ca + ac) * rootinverse;
        quaternion[3] = (bc - cb) * rootinverse;

        return quaternion;
    }

    if (bb > cc) {

        root = Math.sqrt(1 + bb - aa - cc) * 2;
        rootinverse = 1 / root;

        quaternion[0] = (ab + ba) * rootinverse;
        quaternion[1] = 0.25 * root;
        quaternion[2] = (bc + cb) * rootinverse;
        quaternion[3] = (ca - ac) * rootinverse;

        return quaternion;
    }

    root = Math.sqrt(1 + cc - aa - bb) * 2;
    rootinverse = 1 / root;

    quaternion[0] = (ca + ac) * rootinverse;
    quaternion[1] = (bc + cb) * rootinverse;
    quaternion[2] = 0.25 * root;
    quaternion[3] = (ab - ba) * rootinverse;

    return quaternion;
}

/**
 * Gets a new scale vector from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @returns {vector3}
 */
function getscale($matrix) {

    const vector = VECTOR3.identity();

    const aa = $matrix[0];
    const ab = $matrix[1];
    const ac = $matrix[2];

    const ba = $matrix[4];
    const bb = $matrix[5];
    const bc = $matrix[6];

    const ca = $matrix[8];
    const cb = $matrix[9];
    const cc = $matrix[10];

    vector[0] = Math.hypot(aa, ab, ac);
    vector[1] = Math.hypot(ba, bb, bc);
    vector[2] = Math.hypot(ca, cb, cc);

    return vector;
}

/**
 * Gets a new translation vector from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @returns {vector3}
 */
function gettranslation($matrix) {

    const vector = VECTOR3.identity();

    vector[0] = $matrix[12];
    vector[1] = $matrix[13];
    vector[2] = $matrix[14];

    return vector;
}

/**
 * Gets a new 4x4 identity matrix.
 * @returns {matrix4}
 */
function identity() {

    return [

        1, 0, 0, 0,
        0, 1, 0, 0,
        0, 0, 1, 0,
        0, 0, 0, 1
    ];
}

/**
 * Gets a new 4x4 inverted matrix from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @returns {matrix4}
 */
function invert($matrix) {

    const [

        aa, ab, ac, ad,
        ba, bb, bc, bd,
        ca, cb, cc, cd,
        da, db, dc, dd

    ] = $matrix;

    const eeff = aa * bb - ab * ba;
    const eefg = aa * bc - ac * ba;
    const eefh = aa * bd - ad * ba;
    const effg = ab * bc - ac * bb;
    const effh = ab * bd - ad * bb;
    const egfh = ac * bd - ad * bc;

    const gehf = ca * db - cb * da;
    const gehg = ca * dc - cc * da;
    const gehh = ca * dd - cd * da;
    const gfhg = cb * dc - cc * db;
    const gfhh = cb * dd - cd * db;
    const gghh = cc * dd - cd * dc;

    const determinant = eeff * gghh - eefg * gfhh + eefh * gfhg + effg * gehh - effh * gehg + egfh * gehf;

    let matrix = identity();

    if (determinant === 0) {

        return clone(matrix);
    }

    const determinantinverse = 1 / determinant;

    matrix[0] = (bb * gghh - bc * gfhh + bd * gfhg) * determinantinverse;
    matrix[1] = (ac * gfhh - ab * gghh - ad * gfhg) * determinantinverse;
    matrix[2] = (db * egfh - dc * effh + dd * effg) * determinantinverse;
    matrix[3] = (cc * effh - cb * egfh - cd * effg) * determinantinverse;

    matrix[4] = (bc * gehh - ba * gghh - bd * gehg) * determinantinverse;
    matrix[5] = (aa * gghh - ac * gehh + ad * gehg) * determinantinverse;
    matrix[6] = (dc * eefh - da * egfh - dd * eefg) * determinantinverse;
    matrix[7] = (ca * egfh - cc * eefh + cd * eefg) * determinantinverse;

    matrix[8] = (ba * gfhh - bb * gehh + bd * gehf) * determinantinverse;
    matrix[9] = (ab * gehh - aa * gfhh - ad * gehf) * determinantinverse;
    matrix[10] = (da * effh - db * eefh + dd * eeff) * determinantinverse;
    matrix[11] = (cb * eefh - ca * effh - cd * eeff) * determinantinverse;

    matrix[12] = (bb * gehg - ba * gfhg - bc * gehf) * determinantinverse;
    matrix[13] = (aa * gfhg - ab * gehg + ac * gehf) * determinantinverse;
    matrix[14] = (db * eefg - da * effg - dc * eeff) * determinantinverse;
    matrix[15] = (ca * effg - cb * eefg + cc * eeff) * determinantinverse;

    return matrix;
}

/**
 * Gets a new 4x4 lookat matrix from given reference matrix and its target.
 * @param {matrix4} $matrix The reference matrix.
 * @param {vector3} $target The target position vector.
 * @param {vector3} $target The up vector used when looking at the target matrix.
 * @returns {matrix4}
 */
function lookat($matrix, $target, $up) {

    const matrix = identity();

    const sourcex = $matrix[12];
    const sourcey = $matrix[13];
    const sourcez = $matrix[14];

    const upx = $up[0];
    const upy = $up[1];
    const upz = $up[2];

    let dx = sourcex - $target[0];
    let dy = sourcey - $target[1];
    let dz = sourcez - $target[2];

    let length = dx * dx + dy * dy + dz * dz;

    if (length > 0) {

        length = 1 / Math.sqrt(length);

        dx *= length;
        dy *= length;
        dz *= length;
    }

    let ax = upy * dz - upz * dy;
    let ay = upz * dx - upx * dz;
    let az = upx * dy - upy * dx;

    length = ax * ax + ay * ay + az * az;

    if (length > 0) {

        length = 1 / Math.sqrt(length);

        ax *= length;
        ay *= length;
        az *= length;
    }

    matrix[0] = ax;
    matrix[1] = ay;
    matrix[2] = az;
    matrix[3] = 0;

    matrix[4] = dy * az - dz * ay;
    matrix[5] = dz * ax - dx * az;
    matrix[6] = dx * ay - dy * ax;
    matrix[7] = 0;

    matrix[8] = dx;
    matrix[9] = dy;
    matrix[10] = dz;
    matrix[11] = 0;

    matrix[12] = sourcex;
    matrix[13] = sourcey;
    matrix[14] = sourcez;
    matrix[15] = 1;

    return matrix;
}

/**
 * Gets a new 4x4 multiplied matrix from two given matrices.
 * @param {matrix4} $matrixa The matrix to multiply on.
 * @param {matrix4} $matrixb The matrix to multiply with.
 * @returns {matrix4}
 */
function multiply($matrixa, $matrixb) {

    const [

        aa, ab, ac, ad,
        ba, bb, bc, bd,
        ca, cb, cc, cd,
        da, db, dc, dd

    ] = $matrixa;

    const [

        ee, ef, eg, eh,
        fe, ff, fg, fh,
        ge, gf, gg, gh,
        he, hf, hg, hh

    ] = $matrixb;

    const matrix = identity();

    matrix[0] = aa * ee + ba * ef + ca * eg + da * eh;
    matrix[1] = ab * ee + bb * ef + cb * eg + db * eh;
    matrix[2] = ac * ee + bc * ef + cc * eg + dc * eh;
    matrix[3] = ad * ee + bd * ef + cd * eg + dd * eh;

    matrix[4] = aa * fe + ba * ff + ca * fg + da * fh;
    matrix[5] = ab * fe + bb * ff + cb * fg + db * fh;
    matrix[6] = ac * fe + bc * ff + cc * fg + dc * fh;
    matrix[7] = ad * fe + bd * ff + cd * fg + dd * fh;

    matrix[8] = aa * ge + ba * gf + ca * gg + da * gh;
    matrix[9] = ab * ge + bb * gf + cb * gg + db * gh;
    matrix[10] = ac * ge + bc * gf + cc * gg + dc * gh;
    matrix[11] = ad * ge + bd * gf + cd * gg + dd * gh;

    matrix[12] = aa * he + ba * hf + ca * hg + da * hh;
    matrix[13] = ab * he + bb * hf + cb * hg + db * hh;
    matrix[14] = ac * he + bc * hf + cc * hg + dc * hh;
    matrix[15] = ad * he + bd * hf + cd * hg + dd * hh;

    return matrix;
}

/**
 * Gets a new 4x4 orthographic projection matrix.
 * @param {number} $left The left bound of the frustum.
 * @param {number} $right The right bound of the frustum.
 * @param {number} $bottom The bottom bound of the frustum.
 * @param {number} $top The top bound of the frustum.
 * @param {number} $near The near bound of the frustum.
 * @param {number} $far The far bound of the frustum.
 * @returns {matrix4}
 */
function orthographic($left, $right, $bottom, $top, $near, $far) {

    const dx = 1 / ($left - $right);
    const dy = 1 / ($bottom - $top);
    const dz = 1 / ($near - $far);

    const matrix = identity();

    matrix[0] = -2 * dx;
    matrix[1] = 0;
    matrix[2] = 0;
    matrix[3] = 0;

    matrix[4] = 0;
    matrix[5] = -2 * dy;
    matrix[6] = 0;
    matrix[7] = 0;

    matrix[8] = 0;
    matrix[9] = 0;
    matrix[10] = 2 * dz;
    matrix[11] = 0;

    matrix[12] = ($left + $right) * dx;
    matrix[13] = ($top + $bottom) * dy;
    matrix[14] = ($far + $near) * dz;
    matrix[15] = 1;

    return matrix;
}

/**
 * Gets a new 4x4 perspective projection matrix.
 * @param {number} $angle The field of view in radians.
 * @param {number} $aspect The aspect ratio of the field of view (width / height).
 * @param {number} $near The distance to the near clipping plane.
 * @param {number} [$far] The distance to the far clipping plane (infinity if not given).
 * @returns {matrix4}
 */
function perspective($angle, $aspect, $near, $far) {

    const tangent = Math.tan((Math.PI - $angle) * 0.5);

    const matrix = identity();

    matrix[0] = tangent / $aspect;
    matrix[1] = 0;
    matrix[2] = 0;
    matrix[3] = 0;

    matrix[4] = 0;
    matrix[5] = tangent;
    matrix[6] = 0;
    matrix[7] = 0;

    matrix[8] = 0;
    matrix[9] = 0;
    matrix[10] = -1;
    matrix[11] = -1;

    matrix[12] = 0;
    matrix[13] = 0;
    matrix[14] = -2 * $near;
    matrix[15] = 0;

    if (typeof $far === 'number') {

        const range = $near - $far;
        const rangeinverse = 1 / range;

        matrix[10] = ($near + $far) * rangeinverse;
        matrix[14] = $near * $far * 2 * rangeinverse;
    }

    return matrix;
}

/**
 * Gets a new 4x4 rotated matrix on the x-axis from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @param {number} $angle The angle of rotation in radians to apply.
 * @returns {matrix4}
 */
function rotatex($matrix, $angle) {

    const cosine = Math.cos($angle);
    const sine = Math.sin($angle);

    const ab = $matrix[1];
    const ac = $matrix[2];
    const bb = $matrix[5];
    const bc = $matrix[6];
    const cb = $matrix[9];
    const cc = $matrix[10];

    const matrix = clone($matrix);

    matrix[1] = ab * cosine - ac * sine;
    matrix[2] = ac * cosine + ab * sine;
    matrix[5] = bb * cosine - bc * sine;
    matrix[6] = bc * cosine + bb * sine;
    matrix[9] = cb * cosine - cc * sine;
    matrix[10] = cc * cosine + cb * sine;

    return matrix;
}

/**
 * Gets a new 4x4 rotated matrix on the y-axis from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @param {number} $angle The angle of rotation in radians to apply.
 * @returns {matrix4}
 */
function rotatey($matrix, $angle) {

    const cosine = Math.cos($angle);
    const sine = Math.sin($angle);

    const aa = $matrix[0];
    const ac = $matrix[2];
    const ba = $matrix[4];
    const bc = $matrix[6];
    const ca = $matrix[8];
    const cc = $matrix[10];

    const matrix = clone($matrix);

    matrix[0] = aa * cosine + ac * sine;
    matrix[2] = ac * cosine - aa * sine;
    matrix[4] = ba * cosine + bc * sine;
    matrix[6] = bc * cosine - ba * sine;
    matrix[8] = ca * cosine + cc * sine;
    matrix[10] = cc * cosine - ca * sine;

    return matrix;
}

/**
 * Gets a new 4x4 rotated matrix on the z-axis from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @param {number} $angle The angle of rotation in radians to apply.
 * @returns {matrix4}
 */
function rotatez($matrix, $angle) {

    const cosine = Math.cos($angle);
    const sine = Math.sin($angle);

    const aa = $matrix[0];
    const ab = $matrix[1];
    const ba = $matrix[4];
    const bb = $matrix[5];
    const ca = $matrix[8];
    const cb = $matrix[9];

    const matrix = clone($matrix);

    matrix[0] = aa * cosine - ab * sine;
    matrix[1] = ab * cosine + aa * sine;
    matrix[4] = ba * cosine - bb * sine;
    matrix[5] = bb * cosine + ba * sine;
    matrix[8] = ca * cosine - cb * sine;
    matrix[9] = cb * cosine + ca * sine;

    return matrix;
}

/**
 * Gets a new 4x4 scaled matrix from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @param {vector3} $vector3 The scale to apply.
 * @returns {matrix4}
 */
function scale($matrix, $vector3) {

    const [x, y, z] = $vector3;

    const [

        aa, ab, ac, ad,
        ba, bb, bc, bd,
        ca, cb, cc, cd,
        da, db, dc, dd

    ] = $matrix;

    const matrix = identity();

    matrix[0] = aa * x;
    matrix[1] = ab * x;
    matrix[2] = ac * x;
    matrix[3] = ad * x;

    matrix[4] = ba * y;
    matrix[5] = bb * y;
    matrix[6] = bc * y;
    matrix[7] = bd * y;

    matrix[8] = ca * z;
    matrix[9] = cb * z;
    matrix[10] = cc * z;
    matrix[11] = cd * z;

    matrix[12] = da;
    matrix[13] = db;
    matrix[14] = dc;
    matrix[15] = dd;

    return matrix;
}

/**
 * Gets a new 4x4 translated matrix from given reference matrix.
 * @param {matrix4} $matrix The reference matrix.
 * @param {vector3} $vector3 The translation to apply.
 * @returns {matrix4}
 */
function translate($matrix, $vector3) {

    const [x, y, z] = $vector3;

    const [

        aa, ab, ac, ad,
        ba, bb, bc, bd,
        ca, cb, cc, cd,
        da, db, dc, dd

    ] = $matrix;

    const matrix = clone($matrix);

    matrix[12] = aa * x + ba * y + ca * z + da;
    matrix[13] = ab * x + bb * y + cb * z + db;
    matrix[14] = ac * x + bc * y + cc * z + dc;
    matrix[15] = ad * x + bd * y + cd * z + dd;

    return matrix;
}

export {

    clone,
    create,
    fromquaternion,
    getrotation,
    getscale,
    gettranslation,
    identity,
    invert,
    lookat,
    multiply,
    orthographic,
    perspective,
    rotatex,
    rotatey,
    rotatez,
    scale,
    translate
};
